import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

export type ArrangeContextTarget =
  | { kind: "pattern"; id: string }
  | { kind: "track"; patternId: string; track: number }
  | { kind: "loopBrace" };

export type ArrangeMenuAction =
  | "rename"
  | "duplicate"
  | "delete"
  | "copy"
  | "paste"
  | "copyTrack"
  | "pasteTrack"
  | "bounceToWav"
  | "bounceToSampler";

type ArrangeContextMenuProps = {
  target: ArrangeContextTarget;
  point: { x: number; y: number };
  canDelete: boolean;
  canPastePattern: boolean;
  canPasteTrack: boolean;
  /** Audio must be armed for offline bounce. */
  canBounce: boolean;
  onAction: (action: ArrangeMenuAction) => void;
  onClose: () => void;
};

/**
 * Portaled arrange context menu (pattern, track cell, or loop brace).
 */
export function ArrangeContextMenu({
  target,
  point,
  canDelete,
  canPastePattern,
  canPasteTrack,
  canBounce,
  onAction,
  onClose,
}: ArrangeContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) {
      return;
    }
    const margin = 8;
    const width = menu.offsetWidth || 160;
    const height = menu.offsetHeight || 120;
    const left = Math.min(
      Math.max(margin, point.x),
      Math.max(margin, window.innerWidth - width - margin),
    );
    const top = Math.min(
      Math.max(margin, point.y),
      Math.max(margin, window.innerHeight - height - margin),
    );
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }, [point.x, point.y]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const isPattern = target.kind === "pattern";
  const isLoopBrace = target.kind === "loopBrace";

  return createPortal(
    <>
      <div
        className="pointer-events-auto fixed inset-0 z-[200] m-0 cursor-default border-none bg-transparent p-0"
        onPointerDown={event => {
          event.preventDefault();
          onClose();
        }}
        onContextMenu={event => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="pointer-events-auto fixed z-[201] min-w-[9.5rem] max-w-[calc(100vw-16px)] rounded-md border border-white/10 bg-lab-panel p-0.5 text-xs text-slate-200 shadow-[0_12px_28px_rgba(0,0,0,0.35)]"
        role="menu"
        style={{ left: point.x, top: point.y }}
      >
        {isLoopBrace ? (
          <>
            <MenuItem
              disabled={!canBounce}
              onClick={() => {
                onClose();
                onAction("bounceToWav");
              }}
            >
              Bounce to WAV
            </MenuItem>
            <MenuItem
              disabled={!canBounce}
              onClick={() => {
                onClose();
                onAction("bounceToSampler");
              }}
            >
              Bounce to sampler
            </MenuItem>
          </>
        ) : isPattern ? (
          <>
            <MenuItem
              onClick={() => {
                onClose();
                onAction("rename");
              }}
            >
              Rename
            </MenuItem>
            <MenuItem
              onClick={() => {
                onClose();
                onAction("duplicate");
              }}
            >
              Duplicate
              <span className="float-right ml-4 text-lab-muted">⌘D</span>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onClose();
                onAction("copy");
              }}
            >
              Copy
            </MenuItem>
            <MenuItem
              disabled={!canPastePattern}
              onClick={() => {
                onClose();
                onAction("paste");
              }}
            >
              Paste
            </MenuItem>
            <MenuItem
              disabled={!canDelete}
              onClick={() => {
                onClose();
                onAction("delete");
              }}
            >
              Delete
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                onClose();
                onAction("copyTrack");
              }}
            >
              Copy track
            </MenuItem>
            <MenuItem
              disabled={!canPasteTrack}
              onClick={() => {
                onClose();
                onAction("pasteTrack");
              }}
            >
              Paste track
            </MenuItem>
          </>
        )}
      </div>
    </>,
    document.body,
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="block h-8 w-full rounded border-none bg-transparent px-[0.55rem] text-left text-inherit [font:inherit] hover:enabled:bg-white/8 disabled:cursor-not-allowed disabled:text-slate-500"
      role="menuitem"
      disabled={disabled}
      onMouseDown={event => {
        if (!disabled) {
          event.preventDefault();
        }
      }}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) {
          onClick();
        }
      }}
    >
      {children}
    </button>
  );
}
